import {
  cloneElement,
  isValidElement,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { CircularProgress } from "@/components/ui/circular-progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IconButton } from "@/components/ui/icon-button";
import { menuTriggerPressProps } from "@/components/ui/menu-trigger-press";
import {
  IconMenuContext,
  OPENED_BY,
  iconMenuModalityProps,
  triggerOf,
  useIconMenu,
} from "@/components/ui/icon-menu-context";
import { MuiDivider } from "@/components/ui/mui-divider";
import { MuiTooltip } from "@/components/ui/mui-tooltip";
import { spareFromRadixDismiss } from "@/components/ui/radix-dismiss";
import { composeRippleProps, useTouchRipple } from "@/components/touch-ripple";
import { cn } from "@/lib/utils";
import { pinExitStart, setGrowClock } from "@/components/ui/mui-grow";
import {
  DIALOG_LAYER,
  POPPER_LAYER,
  useBackdropPressDismiss,
} from "@/components/ui/mui-backdrop-press";

/**
 * The root both item menus hang from — the donor's `MenuButton`/`ActionMenu`
 * shell, which is a MUI `Menu` and so a `Modal`.
 *
 * Controlled, so `IconMenuButtonContent` can close it without asking Radix's
 * layer stack for permission (see the context above). `defaultOpen` is honoured
 * for the tests that render an already-open menu; `open`/`onOpenChange` stay
 * available for a caller that wants to drive it, and are composed rather than
 * replaced.
 */
export function IconMenuButton({
  children,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof DropdownMenu>) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const [openings, setOpenings] = useState(0);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const contentRef = useRef<HTMLDivElement>(null);

  const setOpen = useCallback(
    (next: boolean) => {
      // A close asked for twice is one close. Radix asks again when its
      // still-mounted layer hears an `Escape` during the exit (see
      // `spareFromRadixDismiss`), and a caller's `onOpenChange` should hear the
      // menu close once, not once per layer that noticed.
      if (next === open) return;
      // Every close funnels through here — a row chosen, a press outside, the
      // `Escape` below, a caller's own `onOpenChange` — so this is the one place
      // that always runs while the paper is still in its opened state.
      //
      // Only on the way *out of* open. A second `Escape` during the exit calls
      // this again, and re-pinning then would re-resolve the running keyframes
      // from the paper's half-shrunk size and restart the shrink. The donor's
      // second `Escape` is inert.
      if (!next && open) pinExitStart(contentRef.current);
      if (next) setOpenings((count) => count + 1);
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange, open],
  );

  // **The keyboard goes back to the trigger in the commit that closes the
  // menu**, while the paper has only begun to leave (#718 gap 2). MUI's
  // `FocusTrap` restores the node it recorded on the way in from its `[open]`
  // effect's cleanup (`FocusTrap.js:144-151`), and React flushes that in the
  // same task as the event that closed the menu — a row chosen, `Escape`,
  // `Tab`, a release outside. What it recorded is the `relatedTarget` of the
  // first focus into the trap: the row the menu opened on took focus from the
  // trigger, so the trigger is what comes back. Radix returns focus only when
  // the paper unmounts, 200–300ms later, and every key in between landed on a
  // row that had already been chosen — a second Enter choosing it again.
  //
  // A passive effect, not a layout one: Radix's focus scope stays armed until
  // its own `trapped` effect is cleaned up — a passive cleanup of this same
  // commit — and a focus moved out before then is pulled straight back onto
  // the chosen row. Every passive cleanup runs before any passive setup, so
  // this lands after the trap has let go, and still in the close commit, as
  // MUI's cleanup does.
  //
  // Only while focus is still the menu's — on the paper, or dropped on the body.
  // `FocusTrap` asks nothing, but it never needs to: in the donor the dialog a
  // row opens mounts after this restore. Here Radix runs a row's `onSelect`
  // inside `flushSync`, so that dialog has already committed and taken focus by
  // the time the menu's close commits, and a restore then would pull focus out
  // from under it. The dialog records the trigger regardless — see `AppDialog`.
  // Radix's own refocus at unmount is declined by the content, since by then
  // the reader may have moved on.
  const wasOpen = useRef(open);
  useEffect(() => {
    const closing = wasOpen.current && !open;
    wasOpen.current = open;
    if (!closing) return;
    const content = contentRef.current;
    if (!content) return;
    const active = document.activeElement;
    if (active !== null && active !== document.body && !content.contains(active)) return;
    triggerOf(content)?.focus();
  }, [open]);

  const close = useCallback(() => setOpen(false), [setOpen]);
  const toggle = useCallback(() => setOpen(!open), [setOpen, open]);
  const registerContent = useCallback((node: HTMLDivElement | null) => {
    contentRef.current = node;
  }, []);
  const menu = useMemo(
    () => ({ open, close, toggle, registerContent, openings }),
    [open, close, toggle, registerContent, openings],
  );

  return (
    <IconMenuContext.Provider value={menu}>
      <DropdownMenu open={open} onOpenChange={setOpen} {...props}>
        {children}
      </DropdownMenu>
    </IconMenuContext.Provider>
  );
}

/**
 * What an item menu shows in place of its rows while the read they wait on is
 * in flight: a 20px spinner, centred, 12px above and below. The trigger stays.
 *
 * Both donor menus draw it identically: the details page's while its usage
 * loads (`ActionMenu.tsx:157-163`), and the item-search card's while its
 * launched usage read is in flight (`ActionsMenu.tsx:149-155`). Each is a
 * `Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center',
 * py: 1.5 }}` around `<CircularProgress size={20} />`.
 */
export function IconMenuLoading() {
  return (
    <div data-testid="icon-menu-loading" className="flex items-center justify-center py-3">
      <CircularProgress size={20} />
    </div>
  );
}

/**
 * `MoreVertIcon` itself, inlined — three filled circles of radius 2 at cy 6, 12
 * and 18 in a 24 viewBox (`@mui/icons-material/MoreVert.js:11-13`), which is a
 * 4×16 glyph on a 6px pitch. The icon set's `EllipsisVertical` is radius 1 at
 * cy 5, 12 and 19 stroked at 2 — 4×18 on a 7px pitch, two pixels taller and
 * visibly looser beside the donor's on the same 34px button. Inlined the way
 * `chip-list.tsx` inlines Material's `cancel`, because the shape is the
 * source's.
 */
export function MoreVertGlyph({ size = "medium" }: { size?: "small" | "medium" } = {}) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      // MUI's `fontSize`: `medium` 24px, `small` 20px — the communities list's
      // `<MoreVertIcon fontSize='small'/>` (`communities-data-table.tsx`).
      className={cn(size === "small" ? "size-5" : "size-6", "fill-current")}
    >
      <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2m0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2m0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2" />
    </svg>
  );
}

/**
 * The donor's `MenuButton` rendered with no children — an icon button that
 * opens a menu below itself, flush to its right edge. Four boxes and a
 * caller-supplied glyph, both below.
 *
 * `packages/react-ui/src/menu-button/menu-button.tsx` hands `MoreVertIcon` to
 * `packages/react-ui/src/button/button.tsx`, whose `children === undefined &&
 * startIcon !== undefined` arm (`:50-65`) renders a MUI `IconButton`. The menu
 * is anchored bottom/right → top/right, which is a menu hanging below the
 * trigger with its right edges aligned.
 *
 * **Four boxes, named** (the first two by owner ruling on #456, decision D1).
 * That ticket described one "small, outlined" trigger shared by both screens;
 * the source has more than one, and `variant='outlined'` is not among them —
 * MUI 9's `IconButton` has no `variant` prop, no `variants` entry answers to it
 * and the donor theme adds no `MuiIconButton` override, so
 * `item-actions-button.tsx:108` lands on the DOM as an inert attribute:
 *
 *   - `outlined` — the details page (`ItemDetailsIsland/components/Header/
 *     ActionMenu.tsx:61-66`): 5px corners, a 1px border in the image-box border
 *     token, 4px of padding, `action.active` ink.
 *   - `plain` — the catalog list (`item-actions-button.tsx:106-111`, a stock
 *     `size='small' color='inherit'` `IconButton`): a circle, no border, 5px of
 *     padding (`IconButton.js:130`), and the surrounding text's colour
 *     (`:111-114`).
 *   - `card-corner` — the Item Search island card's kebab (#798), a squeezed
 *     `medium` button on a translucent white ground over the card's picture.
 *   - `default` — the items batch toolbar's gear (`items-toolbar.tsx:69-76`),
 *     which passes no `size` at all and so lands on MUI's own `medium`:
 *     measured on the running donor at 40×40, `padding: 8px`, a circle,
 *     `rgba(0, 0, 0, 0.54)` ink, a 24px glyph. It adds no classes here for
 *     exactly that reason — it is the shared `IconButton`'s own box. It stands
 *     a head taller than the `size='small'` buttons beside it in that bar; the
 *     donor's own look, not a mismatch to tidy away.
 *
 * The first two come out 34×34, and all three take the same hover wash:
 * `--IconButton-hoverBg` is `alpha(action.active, hoverOpacity)` for every
 * colour but a palette one, and `inherit` is not a palette colour
 * (`IconButton.js:66, 108-125`).
 *
 * **The glyph is the caller's, and required for the same reason `box` is.**
 * The donor takes it per call site too (`menu-button.tsx:88`, as `icon`): both
 * item menus draw `MoreVertIcon`, the gear draws `SettingsIcon`. Defaulting to
 * `MoreVertGlyph` would make the gear the accidental exception and let a fifth
 * caller ship the wrong glyph with no type error, which is exactly what the
 * note on `box` below refuses. What must *not* vary is everything else — the
 * press handling, the recorded modality and the menu's own `toggle` — so there
 * is one assembly here rather than a copy per call site. A copy is what #542's
 * parity review round 12 found, and it had already dropped two of the three.
 */
export function IconMenuButtonTrigger({
  label,
  box,
  className,
  children,
  onPointerDown,
  ...props
}: React.ComponentProps<typeof IconButton> & {
  /** The trigger's accessible name — it has no text of its own. */
  label: string;
  /**
   * Which donor box this trigger is. Required, and deliberately not defaulted:
   * no screen's shape is the "normal" one to the exclusion of the others, and a
   * default would make the rest the exception by accident.
   */
  box: "outlined" | "plain" | "default" | "card-corner";
  /** The glyph, per call site the way the donor takes it — see above. */
  children: React.ReactNode;
}) {
  const menu = useIconMenu();
  const modality = iconMenuModalityProps<HTMLButtonElement>();
  /** Whether a Space press began on this button and has not been released. */
  const spacePressed = useRef(false);

  return (
    // `asChild`, so the trigger *is* the shared `IconButton` — which is what
    // the donor renders on both screens (`ActionMenu.tsx:61-66`,
    // `item-actions-button.tsx:106-111`). Before this it was a third hand-built
    // icon button: its own centred ripple, its own hover wash, its own eleven
    // composed arms, all of them the shared component's now. What stays here is
    // what is genuinely this trigger's — the donor boxes, and the press
    // handling that declines Radix's open-on-pointerdown.
    //
    // Radix composes its own handlers *behind* the child's and stands down on a
    // defaultPrevented event, which is the same ordering the props form gave
    // and is exactly what the `preventDefault` below depends on.
    <DropdownMenuTrigger asChild>
      <IconButton
        aria-label={label}
        {...props}
        // **Radix's press-open declined, and a click put in its place** — the
        // donor opens its menu on release, not on press. Shared with the
        // catalog batch toolbar's gear, whose donor is the same `MenuButton`;
        // `menuTriggerPressProps` holds the reasoning and the event ordering.
        // What is layered on here is this trigger's own: the modality it
        // records for the content to read back.
        {...menuTriggerPressProps<HTMLButtonElement>({
          toggle: menu.toggle,
          onPointerDown: (event) => {
            modality.onPointerDown(event);
            onPointerDown?.(event);
          },
          onClick: props.onClick,
        })}
        // **Enter and Space are a click**, as on the donor's `IconButton`, a
        // native button: Enter clicks it on the way down and Space on the way
        // up, and that click is what opens its menu — through the click path
        // above, so a caller's `onClick` hears it as it does a mouse press.
        // Radix opens on the keydown and sends no click, so the key is taken
        // back from it (Radix composes its arm behind this one and stands down
        // on a prevented event). `IconButton` composes the ripple's arms behind
        // this too.
        //
        // Space clicks only on the release of this button's own press, as a
        // native button's does: a press begun elsewhere and carried here by
        // Tab, or one whose keydown chose a menu row and handed focus back,
        // does nothing. A blur cancels the press.
        onKeyDown={(event) => {
          modality.onKeyDown(event);
          props.onKeyDown?.(event);
          // **ArrowDown is not a key a native button answers** (#718 gap 1).
          // Every donor trigger is a native button with no key handler, so the
          // menu stays shut and the key keeps its default — the page scroll.
          // Radix's arm opens on it *and* `preventDefault`s it, and stands down
          // only on an event already marked defaulted, so the mark goes on the
          // synthetic event alone: `composeEventHandlers` reads that flag, while
          // the native event underneath is left uncancelled and scrolls. React's
          // `preventDefault` is just this assignment plus the native call
          // (`SyntheticEvent.preventDefault`). No ancestor of any trigger reads
          // the flag for ArrowDown — none sits inside a roving-focus group.
          if (event.key === "ArrowDown") {
            event.defaultPrevented = true;
            return;
          }
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          if (event.key === " " && !event.repeat) spacePressed.current = true;
          // Every Enter, repeats included: Chrome clicks a native button on
          // each repeat of a held key.
          if (event.key === "Enter") event.currentTarget.click();
        }}
        onKeyUp={(event) => {
          props.onKeyUp?.(event);
          if (event.key !== " " || !spacePressed.current) return;
          spacePressed.current = false;
          event.currentTarget.click();
        }}
        onBlur={(event) => {
          spacePressed.current = false;
          props.onBlur?.(event);
        }}
        className={cn(
          // The round box, the hover wash, the centred ripple, the 150ms
          // background transition and the focus ring all come from `IconButton`.
          // What is left here is the part that is this trigger's own: the two
          // donor boxes, neither of which is MUI's default padding.
          box === "outlined" && "border-lennar-image-box-border rounded-[5px] border p-1",
          // `text-lennar-ink` for what the donor writes as `color='inherit'`
          // (`item-actions-button.tsx:107`): MUI's `CssBaseline` puts
          // `text.primary` on the body, so the donor's inherited ink *is*
          // `rgba(0,0,0,0.87)`, where `IconButton`'s own `action.active` is the
          // 54% one. The token is the donor's value named rather than a colour
          // chosen for this trigger. `p-[5px]` is `IconButton`'s `small`, which
          // is what `item-actions-button.tsx:109` passes.
          box === "plain" && "text-lennar-ink p-[5px]",
          // `card-corner` — the Item Search island card's kebab
          // (`ItemSearchIsland/components/ItemCard/ActionsMenu.tsx`): MUI's
          // `medium` `IconButton` squeezed to `height: '1.3rem', width:
          // '1.3rem'` on a `rgba(255, 255, 255, 0.75)` ground, with `mr: -1,
          // ml: 1, mt: 1.2` — so its 8px of padding leave a 4.8px content box
          // and the 24px glyph overflows it, centred, as it does over a picture
          // in the donor. `shrink-0` is `SvgIcon`'s `flexShrink: 0`, which is
          // what keeps the glyph 24px in a box that small.
          box === "card-corner" &&
            "bg-catalog-card-corner-wash mt-[9.6px] -mr-2 ml-2 size-[1.3rem] [&>svg]:shrink-0",
          // `default` adds nothing on purpose: it *is* `IconButton`'s `medium`,
          // which is what the gear's donor lands on by passing no `size`.
          className,
        )}
      >
        {children}
      </IconButton>
    </DropdownMenuTrigger>
  );
}

/** The body mark `index.css` reads to lift Radix's scroll lock — a counter, as
 *  `data-scroll-locked` is, so two menus closing out of order cannot strand it. */
const SCROLL_FREE = "data-menu-scroll-free";

/**
 * **`disableScrollLock`, on a Radix modal menu** (#810) — which locks the
 * page's scroll whenever it is open and offers no prop to stop it: its content
 * wraps itself in `react-remove-scroll` (`disableOutsideScroll`, private to
 * the library), which does two things, and this undoes both while the paper
 * is mounted — open and through its exit, exactly as long as the lock itself:
 *
 *   - **the overflow** — `react-remove-scroll-bar` injects `body[data-scroll-
 *     locked] { overflow: hidden !important; … }` and pads the body by the
 *     scrollbar it removed. The mark below lets `index.css`'s more specific,
 *     unlayered rule put both back;
 *   - **the wheel** — a non-passive `wheel` and `touchmove` listener on
 *     `document` cancels any scroll it judges the paper cannot take, a
 *     gesture over the paper itself included. A capture listener on `window`
 *     runs before it and keeps **every** gesture from reaching it, so the
 *     browser scrolls what it would with no lock — the paper if it can, and
 *     the page past it — as MUI's, which listens for nothing here. **The
 *     cost**: while the paper is mounted no listener below `window` — React's
 *     root included, so no `onWheel` — hears a wheel or a touchmove. Nothing
 *     on the page can take one then anyway: the modal menu makes everything
 *     outside the paper inert, and the paper listens for neither.
 *
 * MUI's `Popover` re-places its paper on every window scroll at
 * `disableScrollLock` (`Popover.js:250-255`), clamped 16px inside the window
 * (`:202-211`); Radix's popper follows its anchor through every scrolling
 * ancestor, and the content's `sticky="always"` is the clamp.
 */
function ReleasedScrollLock() {
  useEffect(() => {
    const body = document.body;
    body.setAttribute(SCROLL_FREE, String(Number(body.getAttribute(SCROLL_FREE) ?? 0) + 1));
    // The menu's own lock only: at a count above one another modal holds the
    // page too, and its listener must hear the gesture (`index.css` reads the
    // same count).
    const letThrough = (event: Event) => {
      if (body.getAttribute("data-scroll-locked") !== "1") return;
      event.stopPropagation();
    };
    const options = { capture: true } as const;
    window.addEventListener("wheel", letThrough, options);
    window.addEventListener("touchmove", letThrough, options);
    return () => {
      window.removeEventListener("wheel", letThrough, options);
      window.removeEventListener("touchmove", letThrough, options);
      const count = Number(body.getAttribute(SCROLL_FREE) ?? 1) - 1;
      if (count > 0) body.setAttribute(SCROLL_FREE, String(count));
      else body.removeAttribute(SCROLL_FREE);
    };
  }, []);
  return null;
}

/**
 * Hands the menu's first enabled row the focus its donor gives it, and reports
 * whether that focus should be a quiet one.
 *
 * A child of the content rather than an `onOpenAutoFocus` handler, because Radix
 * keeps that prop private — `MenuRootContentTypeProps` omits every key of
 * `MenuContentImplPrivateProps`, `onOpenAutoFocus` included. A child works
 * because of how Radix's `FocusScope` opens: it auto-focuses only when the
 * container does not already contain the active element, and a child's effect
 * runs before its parents'. So this focuses a row first, and the scope then
 * leaves it alone.
 */
function InitialRowFocus({
  contentRef,
  onQuietFocus,
}: {
  contentRef: React.RefObject<HTMLDivElement | null>;
  onQuietFocus: (quiet: boolean) => void;
}) {
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    // How *this* menu's trigger was pressed is the signal for whether this
    // focus should be a loud one. MUI wants the same distinction and admits of
    // its own API that it "does not allow a reliable `openInteractionType` for
    // anchored menus" (`MenuItem.js:186-189`), shipping an explicit
    // `suppressInitialFocusVisible` escape hatch instead.
    //
    // Nothing recorded is its own third answer, not a synonym for either: the
    // menu was opened from code, and no interaction reached the trigger to
    // have a modality. Suppressing nothing is right there, because what is
    // left deciding is Chromium's own focus-visible inheritance — programmatic
    // focus is focus-visible when the last interaction was a keyboard one —
    // which is the page-wide modality MUI tracks for itself in
    // `useIsFocusVisible` and reads for exactly this styling.
    const trigger = triggerOf(content);
    const quiet = trigger?.getAttribute(OPENED_BY) === "pointer";

    // The first row that can be chosen, which is the one MUI's `MenuList`
    // picks. #456 D2's ruling is about a blocked row's *reachability* by the
    // arrow walk, not about which row the menu opens on.
    const first = content.querySelector<HTMLElement>(
      "[data-slot=dropdown-menu-item]:not([data-disabled])",
    );
    if (!first) return;

    onQuietFocus(quiet);
    first.focus();

    // From here the browser is right and the mark is only in the way. MUI
    // paints its wash from `ButtonBase`'s `handleFocus` and nowhere else
    // (`ButtonBase.js:229-236`), so the wash arrives with a focus — an arrow,
    // a Home, a typeahead hit — and a key that moves the roving focus nowhere
    // leaves the row as it was. Chromium marks the focused element
    // focus-visible on any keydown, so dropping the mark on keydown darkened
    // a row for a held modifier or a typeahead miss; dropping it on the next
    // focus to land is the donor's own trigger for the wash.
    //
    // Listening after that first `focus()` rather than before is what keeps
    // the initial focus out of it — it is the one this mark exists for.
    const lift = () => onQuietFocus(false);
    content.addEventListener("focusin", lift);

    // And a row is chosen by a press and a release *on that row*, which is
    // all the donor can express: `MenuItem` is a `ButtonBase` whose selection
    // is its `onClick`, and the DOM fires `click` on an element only when the
    // press and the release share it. A press on one row released over
    // another fires the click on the enclosing list instead, which no row's
    // handler ever sees — so the donor chooses nothing and stays open. Nor
    // can the *opening* click choose anything there: `MenuButton` opens from
    // its trigger's `click` (`menu-button.tsx:64-76,93`), by which time the
    // release is over and there is no paper under the cursor at all.
    //
    // Radix is the other way about. Its trigger opens on `pointerdown`, and
    // its rows choose on a `pointerup` the row did *not* see the press of
    // (`react-menu/dist/index.mjs:398-399`) — a press-and-drag affordance —
    // so with a paper lying over its own trigger (`liftIntoView`) the opening
    // click chose whatever row the cursor came to rest on, and a drag across
    // the menu chose the row it was released over.
    //
    // The press is tracked from the document, so that a press *outside* this
    // menu is recorded as what it is rather than leaving the last one standing.
    // `preventDefault` is the library's own opt-out, read by
    // `composeEventHandlers`; `stopPropagation` keeps the event from reaching
    // the root React listens on at all.
    const rowOf = (event: Event) =>
      event.target instanceof Element
        ? event.target.closest("[data-slot=dropdown-menu-item]")
        : null;

    let pressedRow: Element | null = null;
    const notePress = (event: Event) => {
      pressedRow = content.contains(event.target as Node) ? rowOf(event) : null;
    };
    const guardRelease = (event: Event) => {
      const releasedRow = rowOf(event);
      if (releasedRow && releasedRow === pressedRow) return;
      event.preventDefault();
      event.stopPropagation();
    };
    document.addEventListener("pointerdown", notePress, true);
    content.addEventListener("pointerup", guardRelease, true);

    return () => {
      content.removeEventListener("focusin", lift);
      document.removeEventListener("pointerdown", notePress, true);
      content.removeEventListener("pointerup", guardRelease, true);
    };
  }, [contentRef, onQuietFocus]);

  return null;
}

/** MUI's `Popover` `marginThreshold` default (`Popover.js:81`). */
const MARGIN_THRESHOLD = 16;

/**
 * MUI's vertical clamp, which is the half of it Radix does not do:
 * `Popover.js:207-211`. A paper whose bottom would pass `innerHeight -
 * marginThreshold` has the overflow taken back off its `top` and added to its
 * transform origin — so it comes to lie over its own trigger, and the Grow
 * still unfolds from the point the trigger is at.
 *
 * Every input is readable before the paper is painted, which is why this is a
 * ref callback and not an effect: the paper's own height, and the anchor it was
 * given, whose bottom edge *is* its natural top (`sideOffset={0}`). Nothing
 * here waits for Radix to finish positioning, so there is no frame in which
 * the menu is painted hanging off the window.
 *
 * A margin rather than a transform, because the transform is the animation's.
 */
function liftIntoView(node: HTMLElement) {
  const trigger = triggerOf(node);
  if (!trigger) return;

  // `offsetHeight`, not the rect's: the Grow's opening `scale(0.75, 0.5625)`
  // is already on this element by the time the ref callback runs, and a rect
  // is measured through the transform where `offsetHeight` is the layout box.
  const naturalBottom = trigger.getBoundingClientRect().bottom + node.offsetHeight;
  const diff = naturalBottom - (window.innerHeight - MARGIN_THRESHOLD);
  if (diff <= 0) {
    // Cleared, not left behind. This runs again whenever the menu re-renders
    // under a moved anchor or a resized window, and the donor keeps no memory
    // of an earlier clamp either — `Popover.js:276-283` re-enters
    // `getPositioningStyle` on resize and derives `top` and `transformOrigin`
    // from scratch. A lift left on after the room came back is a paper
    // floating above a trigger it should be hanging off.
    node.style.marginTop = "";
    node.style.transformOrigin = "";
    return;
  }

  node.style.marginTop = `${-diff}px`;
  // `right` is both donors' own `transformOrigin.horizontal` (`ActionMenu.tsx:79`,
  // `menu-button.tsx:106-108`), written as the keyword rather than read back off
  // Radix's `--radix-popper-transform-origin`: that variable is not set until
  // Radix has positioned, which is after this runs, and reading it early
  // resolved to the element's default centre — a menu unfolding from the middle
  // of its own top edge.
  node.style.transformOrigin = `right ${diff}px`;
}

/**
 * The same dialogs, minus the ones that hold the page by a mechanism of their
 * own — which is a different question from the one `DIALOG_LAYER` answers, and
 * the two must not be shared.
 *
 * A native modal `<dialog>` is put in the browser's **top layer** by
 * `showModal()` and makes the rest of the document inert from there; it never
 * writes `document.body.style.pointerEvents` and has no Radix cleanup to run.
 * `global-state-overlays.tsx` is one, and it carries an explicit
 * `role="alertdialog"` — so it matched, and a session-expiry or dead-network
 * overlay arriving during a menu's 204–313ms exit deferred the repair to
 * something that would never perform it, leaving the page unclickable for good.
 * Raised by #479's cross-review, run 2.
 *
 * It stays in `DIALOG_LAYER` for the press question, where it is exactly what it
 * looks like: a layer above the menu, not the page behind it.
 */
const PAGE_HOLDING_DIALOG = "[role=dialog]:not(dialog),[role=alertdialog]:not(dialog)";

/**
 * Hands the page back if this menu was the last thing holding it.
 *
 * Radix parks `pointer-events: none` on `document.body` while a layer with
 * `disableOutsidePointerEvents` is up, and `react-menu` passes that flag as
 * `context.open` (`react-menu/dist/index.mjs:138`) — so on a *closing* menu it
 * flips to `false` while the node is still mounted for its exit animation. The
 * flip re-runs Radix's layer effect but does not take the node out of
 * `layersWithOutsidePointerEventsDisabled`; only unmounting does
 * (`react-dismissable-layer/dist/index.mjs:79-92`). A dialog opened from a row
 * and closed inside that window then sees `size === 2` and skips its restore,
 * this menu sees its own flag already `false` and skips its own, and the page is
 * left unclickable with nothing mounted and no way back short of a reload
 * (#479 item 1).
 *
 * Both restores are skipped, so the repair is to notice that nothing is left
 * holding the page and hand it back. Called from the content's ref callback on
 * the `null` call — `IconMenuButtonContent` itself never unmounts, since Radix's
 * `Presence` is what takes the node away, so an unmount effect on the component
 * would never run.
 *
 * A task later, because the node sits inside Radix's popper wrapper and React
 * removes that after the ref has been cleared; a check made synchronously would
 * always find the wrapper and conclude something was still up.
 */
function releasePageOnLastLayerOut() {
  setTimeout(() => {
    if (document.body.style.pointerEvents !== "none") return;
    // Anything still *holding* the page keeps it. A menu closing underneath a
    // dialog that is genuinely still open must not unlock the page behind it,
    // and a second menu opened in the meantime is its own layer.
    //
    // A tooltip is not one of those. Its content is a popper like a menu's and
    // matches the same wrapper attribute, but it is not a dismissable layer with
    // `disableOutsidePointerEvents` and so never restores
    // `document.body.style.pointerEvents` itself — deferring to one would leave
    // the page permanently inert with nothing left to unlock it, which is
    // precisely the failure #479 item 1 exists to close. Radix marks the
    // tooltip's own content with a `role="tooltip"` node inside the wrapper
    // (`react-tooltip/dist/index.mjs:350`), so the wrappers are filtered rather
    // than matched wholesale.
    //
    // Defensive, and deliberately untested: the state cannot be built through
    // the interface today. An open menu has already made the page inert, so no
    // tooltip outside it can be opened at all, and the tooltips *inside* it exit
    // in 150ms against the paper's 196ms floor — MUI's own
    // `getAutoHeightDuration` on the shortest possible paper (#479 parity
    // review, round 6, which reached the same conclusion from the other side).
    // Writing a test for it would mean constructing a DOM the components do not
    // produce.
    //
    // The filter is deliberately coarse in the other direction too, and this is
    // the residue: a popper that is *not* modal never made the page inert and so
    // will never hand it back, but it still matches this wrapper attribute and so
    // still defers the repair. Radix exposes no way to ask a wrapper whether its
    // layer holds `disableOutsidePointerEvents`, and narrowing by role would be
    // wrong — a `Select`'s listbox is modal and must defer. Unreachable through
    // the interface for the same reason as above (a non-modal popper cannot be
    // opened while a menu holds the page), and named for #479's decision round
    // rather than guessed at here. Raised by #479's code review, spec axis.
    //
    // **The opposite mistake — clearing a lock something else still holds — is
    // not open**, which #479's cross-review asked. Only Radix's
    // `DismissableLayer` ever writes `document.body.style.pointerEvents` in this
    // app, and every layer it writes it for renders as one of the two shapes
    // named at `POPPER_LAYER` / `DIALOG_LAYER` above, so a holder this check
    // cannot see does not exist. The coarseness runs one way only: it defers too
    // readily, never clears too readily.
    const holdsThePage = Array.from(document.querySelectorAll(POPPER_LAYER)).some(
      (wrapper) => !wrapper.querySelector("[role=tooltip]"),
    );
    if (holdsThePage || document.querySelector(PAGE_HOLDING_DIALOG)) return;
    document.body.style.pointerEvents = "";
  }, 0);
}

/**
 * The menu the trigger opens: below it, right-aligned, sized to its widest row.
 * Rows keep `DropdownMenuItem`'s 14px by default, which is the catalog list
 * donor's `Typography variant='body2'`; `rowVariant="body1"` is a donor whose
 * rows are bare `MenuItem` text (16px over 24px).
 *
 * **The minimum width is the caller's, through `minWidthPx`.** The details donor
 * sets one explicitly (`ActionMenu.tsx:81-84`) and the list donor sets nothing at
 * all — `menu-button.tsx:97-112` hands the `Menu` no `slotProps`, `PaperProps` or
 * `sx`, so its caller cannot influence the paper and it auto-sizes. A shared 150px
 * lived here until #457's parity review found it padding the list menu with ~13px
 * of trailing whitespace. A prop rather than a class at the call site, and named
 * after the donor's own property, exactly as `MuiTooltip`'s `maxWidthPx` is.
 *
 * **Two row metrics ride here rather than on the rows** (#457). Both donor menus
 * — this one's two callers — draw stock `MuiMenuItem`s, so both want MUI's own
 * numbers, and `dropdown-menu.tsx` is a vendored shadcn primitive this repo does
 * not restyle (`CLAUDE.md`, "Conventions"). A descendant rule on the app-authored
 * box is where a shared look belongs; it also beats the primitive's own
 * utilities on specificity, which a class passed per row would have to fight.
 *
 *   - **16px of horizontal padding.** `MenuItem.js`'s root is `padding: 6px 16px`
 *     where the primitive is `px-2` — a label sitting 8px from the menu's edge
 *     instead of 16px. (`minHeight: 48` in the same block is a red herring: the
 *     `!dense` variant resets it to `auto` at `breakpoints.up('sm')`, so a
 *     desktop row is content height and the primitive's `py-1.5` already matches
 *     the donor's 6px.)
 *   - **0.38 disabled opacity**, `palette.action.disabledOpacity`, where the
 *     primitive bakes in `opacity-50`. `BlockedMenuItem` below used to carry its
 *     own copy of this; it now inherits it, because the silent disables the
 *     catalog list menu draws are ordinary `disabled` rows with no reason to
 *     show and they need the same number.
 *
 * **And no gap under the trigger.** MUI's `Popover` lays the paper's
 * `transformOrigin` point directly on the anchor's `anchorOrigin` point, so
 * top/right onto bottom/right is a menu whose corner touches the trigger's —
 * where the vendored primitive defaults `sideOffset` to 4px. Overridden rather
 * than edited, since it is a prop.
 *
 * **It focuses its first enabled row on open, quietly after a pointer open.**
 * `Menu.js:71-84` defaults `autoFocus` true, so a donor menu always hands focus
 * to a row and the first `ArrowDown` therefore moves to the *second* one. Radix
 * withholds that for a pointer open, so this restores it — and restores the
 * other half with it, which MUI needs its own escape hatch for:
 * `MenuItem.js:186-189`, "when an anchored `<Menu>` is opened with a pointer
 * interaction on a trigger, the item should receive DOM focus but without focus
 * visible styling". Chromium marks a programmatic focus that follows a click as
 * focus-visible, so without the `data-quiet-focus` mark below, every mouse-open
 * would light the first row with the keyboard's 0.12 wash. The mark is dropped
 * the moment a key is pressed, because from then on it *is* a keyboard menu.
 */
export function IconMenuButtonContent({
  minWidthPx,
  alignOffsetPx,
  side = "bottom",
  align = "end",
  rowVariant = "body2",
  scrollLock = true,
  className,
  children,
  onEscapeKeyDown,
  onCloseAutoFocus,
  ...props
}: React.ComponentProps<typeof DropdownMenuContent> & {
  /** The donor's `minWidth`, where a call site's donor sets one. */
  minWidthPx?: number;
  /**
   * How far to pull the paper back along its own right edge, for a call site
   * whose donor anchors somewhere other than the trigger's right edge.
   *
   * The list donor is the default: `anchorOrigin` and `transformOrigin` both
   * `horizontal: 'right'` (`menu-button.tsx:102-108`), which is `align="end"`
   * and no offset. The details donor anchors `horizontal: 'center'` against the
   * same `'right'` transform (`ActionMenu.tsx:78-79`), putting its paper's
   * right edge over the middle of the trigger — so that caller passes half a
   * trigger. A number rather than a named anchor for the reason `minWidthPx` is
   * one: the donor fact belongs to the call site, not to the shared box.
   */
  alignOffsetPx?: number;
  /**
   * The rows' MUI typography: `body2` where the donor wraps each label in a
   * `Typography variant='body2'`, `body1` where its labels are bare `MenuItem`
   * text (the Spec list's, `spec-actions-button.tsx:213-236`).
   */
  rowVariant?: "body1" | "body2";
  /**
   * Which side of the trigger the paper hangs off. Every donor menu hangs
   * below its trigger, except the Item Search island card's kebab, which hangs
   * to its left — `anchorOrigin {top, left}` against `transformOrigin {top,
   * right}` (`ItemSearchIsland/components/ItemCard/ActionsMenu.tsx`, #798),
   * which is `side="left" align="start"`.
   */
  side?: "bottom" | "left";
  /**
   * MUI's `Modal` scroll lock, which every donor item menu keeps — except the
   * Editor's line ⋮, which opens at `disableScrollLock` (`ActionMenu.tsx:117`,
   * #810): there the page keeps its overflow and its wheel, and the paper
   * follows the trigger as the page scrolls. See `ReleasedScrollLock`.
   */
  scrollLock?: boolean;
}) {
  // Whether the row focused on open should carry the focus wash — off for a
  // pointer open, and back on the moment a key is pressed.
  const [quietFocus, setQuietFocus] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const menu = useContext(IconMenuContext);

  // Composed under a bare `DropdownMenu`, this content would quietly lose two of
  // the donor's behaviours — dismissal on the release rather than the press, and
  // the `Escape` that closes through a tooltip layered above it — and look
  // entirely fine doing it. Dev-only, as `router-fallbacks.tsx` does
  // it: a hard failure in the browser during development, and nothing shipped to
  // a user for a mistake no call site currently makes.
  if (import.meta.env.DEV && !menu) {
    throw new Error(
      "IconMenuButtonContent must be composed under <IconMenuButton>, not a bare <DropdownMenu>.",
    );
  }

  // **`Escape` closes this menu whatever is layered above it**, which is the
  // donor's arrangement and not Radix's. MUI hangs the menu's handler on the
  // modal root (`useModal.js:153`, handler at `:104-124`), and one press there
  // closes the menu — and, just as importantly, does **not** close the reason.
  // Its `Tooltip` listens on `document` with no precedence test
  // (`Tooltip.js:444-452`), but that listener never runs: the modal root
  // `stopPropagation()`s (`useModal.js:119-120`, "Swallow the event, in case
  // someone is listening for the escape key on the body"), React's synthetic
  // `stopPropagation` calls the native one
  // (`react-dom/cjs/react-dom.development.js:6885-6893`), and React 18 delegates
  // from the *portal* container (`:11476-11478`, `preparePortalMount` →
  // `listenToAllSupportedEvents`) — which for a `Modal` is `document.body`. The
  // event stops at `body`, one node short of the tooltip's listener.
  //
  // So the donor's reason stands still, fully opaque, while the paper Grows out
  // from under it, and goes in one frame when `Modal` returns null at `exited`
  // (`Modal.js:189`) — taking the row it hangs from with it.
  //
  // Radix mounts the tooltip's content as a dismissable layer above the menu's,
  // and every layer's escape handler bails unless it is the highest
  // (`react-dismissable-layer/dist/index.mjs:59-67`) — so ours needed a second
  // press, landing exactly when the reader has finished reading the refusal, on
  // the keyboard path #456 D2 exists to keep reachable (#479 item 3).
  //
  // Capture phase on the document, so it runs whether focus is on a row, on the
  // box, or on the wrapper a blocked row's tooltip hangs from. Radix's own
  // handler still runs when the menu *is* the highest layer; closing twice is
  // closing once.
  //
  // **`preventDefault` is how the swallow is expressed here.** Radix's every
  // dismissable layer stands down on an already-defaulted press
  // (`react-dismissable-layer/dist/index.mjs:63`), so marking the key consumed
  // is what leaves the reason alone — the donor's outcome, reached through the
  // library's own contract. `stopPropagation` would be the literal translation
  // and the wrong one: MUI swallows at `body`, *after* the press has already
  // reached the focused row and everything below it, where a capture-phase stop
  // on the document would keep it from arriving at all.
  //
  // The gate is `open`, the span MUI's handler acts over. `Modal` keeps its
  // root — and so its `onKeyDown` — mounted through the closing transition
  // (`Modal.js:189`), but a `Menu` sets no `closeAfterTransition`, so the close
  // itself takes it off the modal stack (`useModal.js:100-101`) and the handler
  // bails on `!isTopModal()` (`:115`) for the whole exit — even for a key aimed
  // at a row of the leaving paper (#718, FinalWalk). Without the gate every
  // mounted menu on the page — twenty cards, twenty closed menus — would
  // swallow every `Escape`.
  const close = menu?.close;
  const registerContent = menu?.registerContent;
  const open = menu?.open ?? false;
  useEffect(() => {
    if (!close || !open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      // `useModal.js:104-106` waits for the IME to settle before reading the
      // key, and so does this: mid-composition, `Escape` cancels the
      // composition and must not also close the menu.
      if (event.key !== "Escape" || event.isComposing || event.keyCode === 229) return;
      const content = contentRef.current;
      if (!content) return;
      // **Only a key aimed inside the paper**, which is where MUI's handler
      // lives: a React `onKeyDown` on the modal root, hearing only what
      // bubbles through it. While the menu is open that is nearly every key,
      // since the trap keeps focus inside.
      if (!content.contains(event.target as Node)) return;
      // **And it stands down when it is not the top modal**, which is MUI's own
      // first act: `useModal.js:115` bails on `!isTopModal()` *before* the
      // `stopPropagation()` at `:120`, so a `Dialog` opened over a `Menu` takes
      // `Escape` and the menu never sees it. Without this the swallow was
      // unconditional, and because Radix's layers stand down on
      // `event.defaultPrevented` (`react-dismissable-layer/dist/index.mjs:63`)
      // a dialog standing over an open menu was left open with the press spent.
      //
      // MUI reads its modal manager's stack; the DOM-order equivalent is that
      // layers portal into `document.body` in mount order, so a dialog mounted
      // after this menu follows its content. Tooltips are deliberately not
      // consulted — they are not modals and are not in MUI's stack either,
      // which is exactly why the donor's menu wins over its own tooltip and why
      // #479 item 3 is one press rather than two. A native `<dialog>` is asked
      // separately: `:modal` means the top layer, which outranks every portal
      // regardless of where in the tree it was rendered.
      const modalAbove =
        document.querySelector("dialog:modal") !== null ||
        Array.from(document.querySelectorAll(DIALOG_LAYER)).some(
          (layer) =>
            (content.compareDocumentPosition(layer) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0,
        );
      if (modalAbove) return;
      event.preventDefault();
      close();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [close, open]);

  // **`Tab` closes it too**, which is the donor's other keyboard dismissal and
  // the one neither the ticket nor its enumeration named (#479's own parity
  // review found it). Both menus here are stock MUI `Menu`s, and
  // `handleListKeyDown` calls `preventDefault()` and then `onClose(event,
  // "tabKeyDown")` (`Menu.js:107-113`, wired onto the `MenuList` slot at
  // `:136-141`). So the key does not move focus onward: the menu closes and
  // `Modal`'s focus restore puts the keyboard back on the trigger. Radix
  // preventDefaults `Tab` and stops there (`react-menu/dist/index.mjs:305`),
  // leaving the menu standing and the key doing nothing at all.
  //
  // Scoped to the content rather than the whole document, because the donor's
  // handler is on the `MenuList` — a `Tab` pressed with focus somewhere else
  // entirely is not this menu's to answer. `contains` rather than an equality
  // test, since focus legitimately sits on a row, or on the wrapper a blocked
  // row's tooltip hangs from.
  //
  // The listener still goes on the document, and reads the ref *at event time*.
  // Binding it to the node instead would need the node to exist when the effect
  // runs, and it does not: this component never unmounts — Radix's `Presence` is
  // what adds and removes the content — so on the render that mounts the paper
  // the effect has already been and gone with a null ref.
  useEffect(() => {
    if (!close) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || event.isComposing || event.keyCode === 229) return;
      if (!contentRef.current?.contains(event.target as Node)) return;
      event.preventDefault();
      close();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [close]);

  // **The release dismisses this menu, not the press** (#479 item 2) — the
  // donor backdrop's click rule, shared with `MuiPopover`; see
  // `useBackdropPressDismiss`.
  useBackdropPressDismiss(open, close);

  // **The page stays inert until the paper has actually gone**, which is the
  // donor's arrangement and the thing #479 item 1 made visible (found by its own
  // parity review). MUI's `Modal` returns `null` only once the transition has
  // `exited` (`Modal.js:189`), so the modal root and its invisible,
  // viewport-covering `Backdrop` — `Popover` forces `invisible: true`
  // (`Popover.js:333-337`) and `Backdrop` is `position: fixed`, all four sides
  // 0, transparent, with no `pointer-events` of its own (`Backdrop.js:39-47`) —
  // stay mounted and hit-testing for the whole exit and absorb any click that
  // lands during it.
  //
  // Radix hands the page straight back instead: `react-menu` passes its layer
  // `disableOutsidePointerEvents: context.open` (`dist/index.mjs:138`), so the
  // flip to `false` restores `document.body.style.pointerEvents` on the spot
  // (`react-dismissable-layer/dist/index.mjs:67-83`) while the paper is still on
  // screen. At the vendored primitive's flat 150ms that was a window; at MUI's
  // height-derived 204–313ms it is long enough to read as "the menu is still
  // there and my click went through it" — a second click landing on a card's
  // item link, or on another card's trigger.
  //
  // Taken back the same way Radix takes it, and given back by the same call that
  // already cleans up after the layer collision: `releasePageOnLastLayerOut`,
  // from the content's ref callback on unmount. The content itself is put back
  // to `auto` so the *paper* keeps hit-testing, as the donor's does — the
  // backdrop absorbs what lands outside it, not what lands on it.
  //
  // In an effect rather than beside `pinExitStart` in `setOpen`, because Radix's
  // restore runs in the same commit: every cleanup fires before any setup, so
  // this runs after it. Writing it during the event handler would just be
  // overwritten.
  useEffect(() => {
    if (open) return;
    const content = contentRef.current;
    if (!content) return;
    document.body.style.pointerEvents = "none";
    content.style.pointerEvents = "auto";
  }, [open]);

  // Set in the ref callback rather than an effect, because that is the last
  // moment before the browser paints — and so before the animation these
  // properties time has started. It is also where `Grow` itself measures:
  // `onEnter` reads `node.clientHeight` and only then writes the transition.
  const measure = (node: HTMLDivElement | null) => {
    contentRef.current = node;
    // The root reads this at close time, to pin where the exit starts from.
    registerContent?.(node);
    if (!node) {
      releasePageOnLastLayerOut();
      return;
    }
    // The lift is a below-the-trigger paper's clamp. A left-hung paper's
    // vertical clamp is Radix's `shift`, along its alignment axis — see the
    // collision padding below.
    if (side === "bottom") liftIntoView(node);
    setGrowClock(node);
  };

  return (
    <DropdownMenuContent
      ref={measure}
      // Radix's still-mounted layer, during the exit. It stays the top layer
      // until it unmounts and `preventDefault`s any `Escape` it hears, so once
      // the menu had closed, a press — at the page behind, or at a row of the
      // leaving paper — was spent on a menu that was already leaving: the
      // failure that reverted #717's first attempt at gap 2. The donor's
      // leaving menu is off its modal stack and hears none of them (the
      // capture handler's gate above). The dismissal Radix still runs is a
      // close of an already-closed menu, which `IconMenuButton`'s `setOpen`
      // ignores.
      onEscapeKeyDown={(event) => {
        onEscapeKeyDown?.(event);
        if (open) return;
        spareFromRadixDismiss(event);
      }}
      // Radix focuses the trigger again when the paper unmounts. The root has
      // already done that on the close, as MUI does, and doing it a second time
      // 200–300ms later drags back a focus the reader has since moved on — a
      // Tab onward from the trigger, a dialog opened from a row.
      onCloseAutoFocus={(event) => {
        onCloseAutoFocus?.(event);
        event.preventDefault();
      }}
      // Declining Radix's press dismissal; the pair rule above is what closes
      // this menu. `onPointerDownOutside` rather than `onInteractOutside`, which
      // is the union of this and `onFocusOutside` — focus leaving the menu is a
      // path the donor does dismiss on, and nothing here changes it.
      onPointerDownOutside={(event) => event.preventDefault()}
      align={align}
      side={side}
      // An unlocked page can scroll the trigger away; MUI's clamp keeps the
      // paper in the window regardless (see `ReleasedScrollLock`). A locked
      // one cannot, so it keeps Radix's default.
      sticky={scrollLock ? "partial" : "always"}
      sideOffset={0}
      alignOffset={alignOffsetPx}
      // MUI's `MenuList` wraps the arrow walk unless asked not to —
      // `disableListWrap = false` becomes `wrap: !disableListWrap`
      // (`MenuList.js:85,116`) — and neither donor menu passes it. Radix
      // defaults `loop` the other way, so without this the shared menu stopped
      // dead at its first and last row where the donor's carries on round.
      loop
      // The window's edges, split between the two libraries because they
      // agree on one axis and not the other. MUI clamps both the same way —
      // `Popover.js:203-226`, four branches, each pushing the paper back
      // inside `marginThreshold` and sliding the Grow's origin by what it
      // moved. Radix's `shift` middleware is that clamp exactly, but only
      // along a bottom-placed paper's *horizontal* axis
      // (`react-popper/dist/index.mjs:106-110`, `crossAxis: false`); the
      // vertical it leaves to `flip`, which puts the menu above the trigger
      // instead — the other side of the button, opening from the other
      // corner, which the donor never does.
      //
      // So the horizontal padding is MUI's `marginThreshold` handed to the
      // middleware that already behaves, and the top is a number no fallback
      // placement can beat: `flip` picks by `bestFit`, so a `top` that
      // overflows by this much is never the better fit and the paper stays
      // below its trigger. What MUI does *next* — the lift — is
      // `liftIntoView`, below.
      //
      // A left-hung paper turns the same reasoning on its side: `shift` runs
      // along its *vertical* alignment axis, so the top and bottom take MUI's
      // margin and the clamp is exact there, and the side `flip` would move it
      // to — the trigger's right — is the one given the unbeatable number.
      collisionPadding={
        side === "left"
          ? {
              top: MARGIN_THRESHOLD,
              right: 1e5,
              bottom: MARGIN_THRESHOLD,
              left: MARGIN_THRESHOLD,
            }
          : {
              top: 1e5,
              right: MARGIN_THRESHOLD,
              bottom: MARGIN_THRESHOLD,
              left: MARGIN_THRESHOLD,
            }
      }
      style={minWidthPx === undefined ? undefined : { minWidth: `${minWidthPx}px` }}
      data-quiet-focus={quietFocus ? "" : undefined}
      className={cn(
        // The paper. `rounded-[4px] border-0 shadow-mui-menu` where the
        // primitive is `rounded-md border shadow-md`, and `px-0 py-2` where it
        // is `p-1` — the horizontal zero is what makes the row's own 16px the
        // label's distance from the menu's edge, and what lets a row's
        // highlight reach that edge.
        // `mui-menu-grow` replaces the primitive's `animate-in` outright — see
        // `index.css`, where it is qualified by `[data-state]` to do that.
        // `min-w-0` drops the primitive's `min-w-[8rem]`: this donor's `Menu`
        // is handed no paper props at all (`menu-button.tsx:97-112`), so it
        // takes its width from its widest row. The one call site whose donor
        // *does* set a minimum passes `minWidthPx`.
        // `text-lennar-ink` because MUI's `Paper` puts `palette.text.primary`
        // on the menu box itself (`Paper.js:49`) and the donor theme declares
        // no `text` override, so the rows are inked `rgba(0,0,0,0.87)` — where
        // the primitive's `text-popover-foreground` is a near-black. The 0.38
        // disabled wash below then lands on the right base.
        "shadow-mui-menu mui-menu-grow text-lennar-ink min-w-0 rounded-[4px] border-0 px-0 py-2",
        // `whiteSpace: 'nowrap'` (`MenuItem.js:75`) — a row keeps its label on
        // one line and the paper widens for it, where the primitive's rows
        // would wrap inside a paper that stopped growing.
        "[&_[data-slot=dropdown-menu-item]]:px-4 [&_[data-slot=dropdown-menu-item]]:whitespace-nowrap",
        "[&_[data-slot=dropdown-menu-item][data-disabled]]:opacity-[0.38]",
        // The rows, continued. Square and full-bleed, a hand on the ones that
        // can be chosen, and MUI's two washes rather than the primitive's one.
        //
        // The primitive paints `focus:bg-accent`, so the first rule clears it
        // and the two washes out-specify that: a descendant selector on this
        // box already beats a row's own utility, and the `:not()` arms lift
        // each wash above the clearing rule. Written so the three selectors
        // cannot overlap rather than as three ordered classes, because
        // Tailwind sorts the class list and source order is not ours to pick.
        //
        // **`:focus-visible` outranks `:hover`, as it does in MUI.** Its rule
        // (`:97-99`) sits after the hover rule (`:76-83`) in the same style
        // object, so a row that is both keyboard-focused and hovered is 0.12
        // there. Now that focus no longer follows the pointer, the two states
        // are independent and the quiet mark partitions them cleanly: with the
        // mark off, focus-visible wins and hover fills in the rest; with it on
        // — the row a pointer open focused, which must stay unwashed — hover is
        // the only wash there is.
        //
        // `text-inherit` because MUI's hover and focus rules change
        // `backgroundColor` and nothing else; the primitive also swaps the
        // label to `text-accent-foreground`, which lightened every focused
        // row's text (measured oklch(0.145) → oklch(0.205)).
        "[&_[data-slot=dropdown-menu-item]]:rounded-none",
        "[&_[data-slot=dropdown-menu-item]:not([data-disabled])]:cursor-pointer",
        "[&_[data-slot=dropdown-menu-item]:focus]:bg-transparent",
        "[&_[data-slot=dropdown-menu-item]:focus]:text-inherit",
        "[&:not([data-quiet-focus])_[data-slot=dropdown-menu-item]:not([data-disabled]):hover:not(:focus-visible)]:bg-lennar-action-hover",
        "[&[data-quiet-focus]_[data-slot=dropdown-menu-item]:not([data-disabled]):hover]:bg-lennar-action-hover",
        "[&:not([data-quiet-focus])_[data-slot=dropdown-menu-item]:not([data-disabled]):focus-visible]:bg-lennar-action-focus",
        rowVariant === "body1" && "[&_[data-slot=dropdown-menu-item]]:text-base",
        className,
      )}
      {...props}
    >
      {/* Keyed on the opening, so a menu reopened inside its own exit — the
          same mounted paper — focuses its first row again (MUI's `MenuList`
          re-runs `focusInitialTarget` on every opening, `MenuList.js:127-169`). */}
      <InitialRowFocus
        key={menu?.openings ?? 0}
        contentRef={contentRef}
        onQuietFocus={setQuietFocus}
      />
      {scrollLock ? null : <ReleasedScrollLock />}
      {children}
    </DropdownMenuContent>
  );
}

/**
 * A row's leading glyph, `lightTextSecondary` with the item's own 8px gap after
 * it — the Spec list's row menu draws one before every label
 * (`spec-actions-button.tsx:213-236`). Decorative: the label names the row.
 *
 * The colour goes on the glyph itself: the vendored item greys any svg without
 * a colour class of its own, over whatever a wrapper says.
 */
export function MenuRowGlyph({
  children,
}: {
  children: React.ReactElement<{ className?: string }>;
}) {
  return (
    <span aria-hidden="true" className="inline-flex">
      {cloneElement(children, {
        className: cn("text-lennar-ink-secondary", children.props.className),
      })}
    </span>
  );
}

/**
 * A row's leading glyph inside MUI's `ListItemIcon` — the communities list's
 * menu, whose rows are `<ListItemIcon><Icon fontSize='small'/></ListItemIcon>
 * <ListItemText>` (`communities-data-table.tsx:226-263`), where the Spec
 * list's draw a bare glyph (`MenuRowGlyph` above).
 *
 * Under a `MenuItem`, MUI gives the icon box `min-width: 36px`
 * (`MenuItem.js`, `& .MuiListItemIcon-root`), in `action.active`
 * (`ListItemIcon.js`), so the label starts 36px from the glyph's left edge
 * whatever the glyph's size. The vendored row puts an 8px gap after every
 * child, so the box takes that back. Decorative: the label names the row.
 */
export function MenuRowListItemIcon({
  children,
}: {
  children: React.ReactElement<{ className?: string }>;
}) {
  return (
    <span aria-hidden="true" className="-mr-2 inline-flex min-w-9 shrink-0">
      {cloneElement(children, {
        className: cn("text-lennar-action-active", children.props.className),
      })}
    </span>
  );
}

/**
 * A rule between two menu rows — MUI's `<Divider />` in a `Menu`, which takes
 * `margin: 8px 0` from the `MenuItem` before it (`MenuItem.js`,
 * `& + .MuiDivider-root`).
 */
export function MenuRowDivider() {
  return <MuiDivider flexItem={false} className="my-2" />;
}

/** Whether a row is a link with an `href` — MUI's native activation. */
function isNativeLink(row: HTMLElement) {
  return row instanceof HTMLAnchorElement && Boolean(row.href);
}

/**
 * **PageUp and PageDown are not menu keys.** MUI's list answers the arrows,
 * Home and End alone (`useRovingTabIndex.js:21`), so the page keys keep their
 * default — the page's scroll where nothing locks it (#810's unlocked line
 * menu), nothing where the lock holds — and the focus stays on its row.
 * Radix's roving focus jumps to the first or last row on them and cancels the
 * key; it stands down on an event already marked defaulted, so the mark goes
 * on the synthetic event alone and the native key is left to scroll — the
 * trigger's `ArrowDown` uses the same mark.
 */
function leavePageKeysTheirDefault(event: React.KeyboardEvent) {
  if (event.key === "PageUp" || event.key === "PageDown") event.defaultPrevented = true;
}

/**
 * A menu row that can be chosen, and inks when it is pressed.
 *
 * `MenuItem` is `styled(ButtonBase)` (`MenuItem.js:57`) with no `disableRipple`,
 * and the donor theme sets none either — no `MuiButtonBase` or `MuiMenuItem`
 * default props, and the string appears nowhere in it. So a donor menu row inks
 * under the finger exactly as its trigger does, and #456 already ported the ink
 * itself (decision D5, keyboard arm included).
 *
 * **Not centred**, unlike the trigger: `MenuItem` passes no `centerRipple`, so
 * the wash starts under the pointer and is sized to reach the row's furthest
 * corner. On a 32px row 150px wide, that is the difference between ink spreading
 * from where the finger landed and a disc blooming out of the middle.
 *
 * The pair to `BlockedMenuItem` below — one row can be chosen, the other cannot,
 * and only the first inks. MUI's disabled `ButtonBase` takes no pointer events
 * at all, so a refused row is silent under the finger as well as to the eye.
 */
export function PressableMenuItem({
  className,
  children,
  asChild,
  onPointerDown,
  onPointerUp,
  onPointerLeave,
  onPointerCancel,
  onPointerMove,
  onKeyDown,
  onKeyUp,
  onFocus,
  onBlur,
  onContextMenu,
  onDragLeave,
  onTouchMove,
  ...props
}: React.ComponentProps<typeof DropdownMenuItem>) {
  const { rippleTargetProps, rippleLayer } = useTouchRipple({ disabled: props.disabled });

  return (
    <DropdownMenuItem
      {...props}
      // Composed, not replaced, and for the reason the trigger's copy of this
      // spells out: the ripple starts on `pointerdown` and is retired by
      // whichever of the other arms arrives, so dropping one leaves ink sitting
      // on the row for good. Radix sends its own handlers through here too.
      // Composed through the shared composer, with **no `focusRipple`** — see
      // the block below the pointer-move arm for why a row must not ink on
      // Space, and `RippleOptions.focusRipple` for the donor roll-call. The
      // gate is what lets this go through the composer at all: before it, the
      // keyboard arms had to be left off by hand, which is why this file once
      // spelled out six arms of its own.
      {...composeRippleProps<HTMLDivElement>(rippleTargetProps, {
        onPointerDown,
        onPointerUp,
        onPointerLeave: (event) => {
          // Defaulted for the same reason `onPointerMove` below is: Radix's
          // arm here hands focus back to the *box* when the pointer leaves a
          // row (`onItemLeave`), so sweeping the mouse over and off a row
          // abandoned the position the arrow keys had built up. MUI moves
          // focus on no pointer event at all.
          event.preventDefault();
          onPointerLeave?.(event);
        },
        onPointerCancel,
        // **Space chooses on its release**, as `useButtonBase.js:143-160` has
        // it: the keydown is only held back and the keyup is the click, where
        // Radix clicks on the keydown. Held back first, so Radix's arm — and
        // the content's typeahead, composed the same way — stands down. A row
        // that is a link with an `href` is MUI's native activation
        // (`:74-89`), and a native anchor has none on Space: held back all
        // the same, and never clicked.
        onKeyDown: (event) => {
          onKeyDown?.(event);
          leavePageKeysTheirDefault(event);
          if (event.key === " " && event.target === event.currentTarget) event.preventDefault();
        },
        onKeyUp: (event) => {
          onKeyUp?.(event);
          const row = event.currentTarget;
          if (
            event.key === " " &&
            !event.defaultPrevented &&
            event.target === row &&
            !isNativeLink(row)
          ) {
            row.click();
          }
        },
        onFocus,
        onBlur,
        onContextMenu,
        onDragLeave,
        onTouchMove,
      })}
      // **Focus does not follow the pointer.** MUI's `MenuItem` has no
      // pointer-move handler at all: hover paints `action.hover` and nothing
      // else, and the roving focus moves on keydown alone. Radix focuses the
      // row under the pointer (`react-menu/dist/index.mjs:444-457`), which gets
      // the donor's keyboard contract wrong in a way a user can act on —
      // arrow-key to one row, slide the mouse across another, press Enter, and
      // the row under the *mouse* fires.
      //
      // `preventDefault` is Radix's own opt-out: the library's arm is composed
      // through `composeEventHandlers`, which skips it once the event is
      // defaulted. All it gives up is submenu grace-period bookkeeping, and
      // these menus have no submenus.
      //
      // Only here, and not on the shared box, because the blunt version broke
      // `BlockedMenuItem`: a tooltip opens on `pointermove` too, and one
      // capture-phase `preventDefault` upstream took the refusal's reason with
      // it. A blocked row needs none of this anyway — `data-disabled` gives it
      // `pointer-events-none`, so Radix's arm never runs there.
      onPointerMove={(event) => {
        event.preventDefault();
        onPointerMove?.(event);
      }}
      // **The keyboard arms carry no ripple**, by the composer's default.
      // `MenuItem` renders its root without `focusRipple`
      // (`MenuItem.js:161-244`), so `ButtonBase.js:105`'s `focusRipple = false`
      // stands and the Space-keydown ink arm (`:152-159`, gated on it) never
      // fires for a menu row. Contrast `IconButton.js:217`, which *does* pass
      // `focusRipple` — keyboard ink is the trigger's behaviour, not a row's,
      // and #456 D5 licensed the hook to *have* that arm, not to apply it
      // where the donor switches it off.
      //
      // So a caller's `onKeyDown`/`onKeyUp` pass through with no ripple arm
      // around them. `onFocus`/`onBlur` still do their remaining job,
      // `ButtonBase.js:221` — retiring ink the pointer left behind when the
      // row loses focus, which is ungated in the donor too.
      className={className}
      asChild={asChild}
    >
      {/* The layer clips itself to the row's own corners, so the row needs
          nothing but the `relative` the primitive already carries.

          Under `asChild` the row *is* the caller's element — an anchor, for the
          two Edit rows, which is what makes middle-click and copy-link work
          (#450 R6). Radix's `Slot` takes exactly one child, so the layer goes
          inside that element rather than beside it. */}
      {asChild && isValidElement<{ children?: React.ReactNode }>(children) ? (
        cloneElement(
          children,
          undefined,
          <>
            {children.props.children}
            {rippleLayer}
          </>,
        )
      ) : (
        <>
          {children}
          {rippleLayer}
        </>
      )}
    </DropdownMenuItem>
  );
}

/**
 * A menu row that cannot be chosen, and the reason for it.
 *
 * The wrapper element is load-bearing, not decoration: a disabled row takes no
 * pointer events, so a tooltip hung on the row itself would never open. The
 * donor hits exactly this and solves it the same way — a `<div>` inside the
 * `Tooltip`, around the disabled `MenuItem` (`ActionMenu.tsx:181, 217`).
 */
export function BlockedMenuItem({
  label,
  icon,
  reason,
  side,
  arrow = false,
  listItemIcon = false,
  testIdPrefix,
  tooltipTestId,
  className,
}: {
  /** The row's text, and the stem of both test ids. */
  label: string;
  /**
   * A leading glyph, the row's `ListItemIcon`-less icon, drawn as
   * `MenuRowGlyph` draws it — or inside a `ListItemIcon` with `listItemIcon`.
   */
  icon?: React.ReactElement<{ className?: string }>;
  /** The donor wraps the glyph in MUI's `ListItemIcon` (`MenuRowListItemIcon`). */
  listItemIcon?: boolean;
  reason: string;
  side: "top" | "bottom";
  /** The donor's `showCallout`, which its two blocked rows disagree about. */
  arrow?: boolean;
  /**
   * Names both test ids: `<prefix>-<label>` on the row, `-tooltip` on the
   * wrapper. Required rather than defaulted, because a default would be one
   * caller's naming frozen into a component every menu shares.
   */
  testIdPrefix: string;
  /**
   * The wrapper's test id, where its donor names it outright — the line menu's
   * held `Add Selections`, `tooltip-disable-selection` (#810).
   */
  tooltipTestId?: string;
  /**
   * The row's own metrics, where its donor row sets them by hand — the Item
   * Search dialog's `Report` at `py: 1.3` (#798).
   */
  className?: string;
}) {
  const reasonId = useId();
  /** True only across the press whose focus move blurs this row — see below. */
  const pressing = useRef(false);
  /**
   * Close the press window on the release.
   *
   * Not the microtask window `MuiTooltip`'s own `markPress` uses. That is
   * enough there because the close Radix asks for is dispatched inside the
   * press itself; the blur declined here is the press's *default action*, so it
   * lands after this handler has returned and a microtask would already have
   * closed. Removed on unmount, and `{ once: true }` on the listeners means a
   * release that has already fired needs no removal.
   *
   * The window's `blur` is the third ender, and it is a backstop rather than a
   * release: a press interrupted by an OS-level focus steal — alt-tab, a native
   * dialog, a screen lock — delivers neither `pointerup` nor `pointercancel`,
   * and the flag would then stay raised for the life of the component and
   * decline every later blur close, leaving the reason box up on a row the
   * reader has arrowed away from.
   */
  const endPress = useCallback(function endPress() {
    pressing.current = false;
    document.removeEventListener("pointerup", endPress);
    document.removeEventListener("pointercancel", endPress);
    window.removeEventListener("blur", endPress);
  }, []);
  useEffect(() => endPress, [endPress]);

  return (
    // **No tooltip provider of its own any more** (#506). One used to sit here
    // and was the whole of #479 item 4's fix: Radix shares one transit area
    // across every tooltip under a `Provider` — `isPointerInTransitRef`
    // (`react-tooltip/dist/index.mjs:36`), armed whenever a grace polygon is
    // drawn and gating the incoming trigger's arm (`:186`) — so a jump between
    // two blocked rows was swallowed and showed nothing at all. MUI has no
    // cross-tooltip arbitration of that kind: the only state its instances
    // share is `hystersisOpen` (`Tooltip.js:210`), which picks an *enter* delay
    // and never silences a peer. `MuiTooltip` now keeps the polygon from ever
    // being drawn, because MUI's leave timer is the close rather than Radix's
    // pointer tracking, so the transit ref is never armed and the root
    // provider serves these rows like any other. Both halves of the donor's
    // behaviour — the jump answers, and the box can be dwelt on — are guarded
    // by test (`icon-menu-button.browser.test.tsx`, "BlockedMenuItem across
    // two refused rows").
    // `holdForKeyboard`: R1 (#506), the owner-ruled break that keeps the
    // refusal a keyboard reader arrowed onto when the parked mouse leaves.
    <MuiTooltip title={reason} side={side} arrow={arrow} asChild holdForKeyboard>
      {/* The wrapper carries the tooltip, exactly as the donor's does — the
          disabled row takes no pointer events, so the wrapper is what a press
          aimed at the row actually hits, and a keyboard `Enter` fires a click
          on the row that bubbles to it.

          Keeping the reason up under it is `MuiTooltip`'s rule now, and
          app-wide (#480): MUI's `Tooltip` attaches no press handler of any
          kind, so no ported screen's tooltip closes on one. This row used to
          buy that with a `preventDefault` of its own, which cost the browser's
          focus move and made this component put it back by hand.

          With the cancel gone the browser does that move itself, which is the
          donor's behaviour unmediated: a press on a disabled row is a press on
          dead space, so focus lands on the nearest focusable ancestor — MUI's
          `ul[role=menu]` and its `tabIndex: -1` (`MenuList.js:240-246`).
          Whatever row the keyboard was on gives up its `action.focus` wash, and
          the roving handler reads the next arrow key as focus-on-container and
          restarts at the first row. */}
      <div
        data-testid={tooltipTestId ?? `${testIdPrefix}-${label}-tooltip`}
        onPointerDown={() => {
          pressing.current = true;
          document.addEventListener("pointerup", endPress, { once: true });
          document.addEventListener("pointercancel", endPress, { once: true });
          window.addEventListener("blur", endPress, { once: true });
        }}
        // Only the blur that focus move just caused. Radix's tooltip closes on
        // its trigger's blur, and #456 D2 put this row inside the menu's
        // roving walk so a keyboard user can reach the refusal at all — which
        // means the row can hold focus, which means the move above blurs it.
        // The donor never meets this: its blocked row is `disabled` and out of
        // the walk, so nothing inside its wrapper can hold focus and the
        // reason survives a press unconditionally. Every other blur still
        // closes the reason, arrowing away included.
        onBlur={(event) => {
          if (pressing.current) event.preventDefault();
        }}
      >
        {/* The tooltip is a *visual* explanation, and Radix hangs its
            `aria-describedby` on the trigger — the wrapper, not the row. A
            screen reader on the focused row would hear the label and nothing
            about the refusal, which is the half of "reachable by keyboard"
            that a sighted user never notices is missing. */}
        <span id={reasonId} hidden>
          {reason}
        </span>
        <DropdownMenuItem
          data-testid={`${testIdPrefix}-${label}`}
          aria-describedby={reasonId}
          // Disabled by hand rather than through the primitive's `disabled`
          // prop, which is the only way the reason is reachable by keyboard:
          // Radix excludes a `disabled` item from the menu's roving focus and
          // swallows Tab outright (`react-menu/dist/index.js:372`), so a
          // `disabled` row can be reached by pointer and by nothing else. Left
          // that way the tooltip explains the refusal to a mouse user and
          // stays silent for a keyboard one. `aria-disabled` is the a11y
          // contract, `data-disabled` drives the primitive's own disabled
          // styling (and its `pointer-events-none`, which is why the tooltip
          // hangs on the wrapper), and `onSelect` is what actually refuses.
          aria-disabled="true"
          data-disabled=""
          onKeyDown={leavePageKeysTheirDefault}
          onSelect={(event) => event.preventDefault()}
          // Reachable, but never *offered*. Losing the primitive's `disabled`
          // prop also loses the three things it suppressed beyond focus, none
          // of which #456 licensed and all of which the donor lacks: the
          // highlight paint under the focused row (a refusal drawn as the
          // chosen row), and typeahead landing on it. An empty `textValue`
          // takes it out of the search — Radix reads `textValue ?? textContent`
          // (`react-menu/dist/index.js`), and `""` is a value.
          textValue=""
          // The disabled *opacity* is no longer here: MUI's 0.38 is every
          // row's number, not this row's, so it moved up to
          // `IconMenuButtonContent` when the catalog list's silently-disabled
          // rows needed it too (#457). What stays is this row's alone — it is
          // hand-disabled so that its reason stays keyboard-reachable, which
          // leaves it inside the roving focus, and a refusal painted as the
          // highlighted row reads as the chosen one.
          className={cn(
            "data-[disabled]:focus:bg-transparent data-[disabled]:focus:text-inherit",
            className,
          )}
        >
          {icon ? (
            listItemIcon ? (
              <MenuRowListItemIcon>{icon}</MenuRowListItemIcon>
            ) : (
              <MenuRowGlyph>{icon}</MenuRowGlyph>
            )
          ) : null}
          {label}
        </DropdownMenuItem>
      </div>
    </MuiTooltip>
  );
}
